/**
 * Traverses an object using a dot-separated path and returns the nested value.
 * Example: getByPath({a: {b: {c: 1}}}, 'a.b.c') => 1
 */
export function getNestedValue(obj, path) {
	if (!obj || !path) return undefined;
	return path
		.split(".")
		.reduce((acc, key) => (acc ? acc[key] : undefined), obj);
}

/**
 * Traverses an object using a dot-separated path and returns the nested value.
 * Supports glob pattern '*' to match any key at that level.
 * Example: getNestedValue({a: {b: {c: 1}}}, 'a.b.c') => 1
 * Example: getNestedValue({a: {b: {c: 1}, d: {c: 2}}}, 'a.*.c') => [1, 2]
 */
export function getNestedValueWithGlobRecursive(obj, path) {
	if (!obj || !path) return undefined;
	const keys = path.split(".");

	function traverse(current, idx) {
		if (idx === keys.length) return current;
		const key = keys[idx];
		if (key === "*") {
			if (typeof current !== "object" || current === null) return undefined;
			const results = Object.values(current)
				.map((v) => traverse(v, idx + 1))
				.filter((v) => v !== undefined);
			return results.length === 1 ? results[0] : results;
		}
		if (current && Object.prototype.hasOwnProperty.call(current, key)) {
			return traverse(current[key], idx + 1);
		}
		return undefined;
	}

	return traverse(obj, 0);
}

export function getNestedValueWithGlob(obj, path) {
	if (!obj || !path) return undefined;
	const keys = path.split(".");
	let currentLevel = [obj];

	for (const key of keys) {
		const nextLevel = [];
		for (const item of currentLevel) {
			if (key === "*") {
				if (item && typeof item === "object") {
					nextLevel.push(...Object.values(item));
				}
			} else if (item && Object.prototype.hasOwnProperty.call(item, key)) {
				nextLevel.push(item[key]);
			}
		}
		if (nextLevel.length === 0) return undefined;
		currentLevel = nextLevel;
	}

	return currentLevel.length === 1 ? currentLevel[0] : currentLevel;
}

export function getLeafs(object, ancestorKeys = []) {
	const leafs = [];
	const keys = Object.keys(object);
	for (const key of keys) {
		const currentValue = object[key];
		const pathArr = [...ancestorKeys, key];
		if (
			!currentValue ||
			(typeof currentValue === "object" && ((Object.keys(currentValue)).length === 0 || currentValue.leaf))
		) {
			leafs.push({
				path: pathArr.join('.'),
				data: currentValue,
			});
			continue;
		}
		leafs.push(...getLeafs(currentValue, pathArr));
	}
	return leafs;
}